/**
 * カスタマイズコードのSAS URLから情報を抽出するユーティリティ
 */

export interface CustomizeCodeFileInfo {
  appId: string;
  fileName: string;
  url: string;
}

/**
 * SAS URLからファイル情報を抽出
 *
 * URL形式: https://account.blob.core.windows.net/customize-code/{org_id}/{project_id}/{job_id}/{app_id}/{file_name}?sv=...
 *
 * @param sasUrl - SAS URL
 * @returns ファイル情報（appId, fileName, url）
 */
export function parseCustomizeCodeUrl(sasUrl: string): CustomizeCodeFileInfo {
  try {
    const url = new URL(sasUrl);
    // /customize-code/{org}/{proj}/{job}/{appId}/{fileName}
    const pathSegments = url.pathname.split("/").filter(Boolean);

    // 最低でも container名 + 4セグメント（org, proj, job, appId, fileName）が必要
    if (pathSegments.length < 6) {
      return {
        appId: "",
        fileName: "",
        url: sasUrl,
      };
    }

    const appId = pathSegments[pathSegments.length - 2] || "";
    const fileName = pathSegments[pathSegments.length - 1] || "";

    return {
      appId,
      fileName,
      url: sasUrl,
    };
  } catch (error) {
    console.error("Failed to parse customize code URL:", error);
    return {
      appId: "",
      fileName: "",
      url: sasUrl,
    };
  }
}

/**
 * SAS URLからコード内容を非同期で取得
 *
 * @param sasUrl - SAS URL
 * @returns コード内容（文字列）
 */
export async function fetchCustomizeCode(sasUrl: string): Promise<string> {
  try {
    const response = await fetch(sasUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    console.error("Failed to fetch customize code:", error);
    throw error;
  }
}

/**
 * SAS URLの期限が切れているかチェック
 *
 * @param sasUrl - SAS URL
 * @returns 期限切れの場合はtrue
 */
export function isSasUrlExpired(sasUrl: string): boolean {
  try {
    const url = new URL(sasUrl);
    const se = url.searchParams.get("se"); // Signature Expiry
    if (!se) {
      return false; // 期限パラメータがない場合は期限切れとみなさない
    }
    const expiryDate = new Date(se);
    return expiryDate < new Date();
  } catch {
    return false;
  }
}

/**
 * ファイルをダウンロード（SAS URLなどクロスオリジン対応）
 *
 * @param url - ダウンロードURL
 * @param fileName - 保存するファイル名
 */
export async function downloadFile(url: string, fileName: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Failed to download file:", error);
    throw error;
  }
}
